/**
 * MenuScene.ts - FF1-style pause menu
 * Status, Items, Magic, Equip, Config screens
 */

import { Scene, GameInput, GameKey, MenuItem } from './types/engine';
import { Renderer } from './Renderer';
import { Menu } from './MenuSystem';
import { Character, ItemType } from '../lib/types/game';
import { InventorySystem } from './InventorySystem';
import { EquipmentSystem } from './EquipmentSystem';
import { getItem } from './data/ItemData';
import { CLASS_INFO } from '../lib/types/game';

type MenuLevel = 'main' | 'status' | 'items' | 'items_target' | 'magic' | 'equip' | 'equip_slot' | 'equip_select' | 'config';

interface MenuState {
  level: MenuLevel;
  selectedCharacter: number;
  returnToWorld: boolean;
  // Items menu state
  itemsMenu?: Menu;
  selectedItem?: number;
  // Equip menu state
  equipSlotMenu?: Menu;
  equipSelectMenu?: Menu;
  selectedEquipSlot?: ItemType;
}

// Callback result for menu state changes
export interface MenuStateResult {
  party: Character[];
  inventory: { itemIds: number[]; quantities: number[] };
}

export class MenuScene implements Scene {
  private renderer: Renderer;
  private state: MenuState;

  // Menus
  private mainMenu: Menu;
  private characterMenu: Menu;

  // Party data
  private party: Character[] = [];

  // Game systems
  private inventory: InventorySystem;

  // Callback for menu close - syncs state back to Game
  private menuCloseCallback?: (result: MenuStateResult) => void;

  // Main menu items
  private readonly MAIN_MENU_ITEMS: MenuItem[] = [
    { label: 'STATUS', enabled: true, value: 'status' },
    { label: 'ITEMS', enabled: true, value: 'items' },
    { label: 'MAGIC', enabled: true, value: 'magic' },
    { label: 'EQUIP', enabled: true, value: 'equip' },
    { label: 'CONFIG', enabled: true, value: 'config' },
  ];

  constructor(ctx: CanvasRenderingContext2D) {
    this.renderer = new Renderer(ctx);

    this.state = {
      level: 'main',
      selectedCharacter: 0,
      returnToWorld: false,
    };

    // Initialize game systems
    this.inventory = new InventorySystem();

    // Create main menu (left side)
    this.mainMenu = new Menu(
      this.MAIN_MENU_ITEMS,
      16,
      40,
      96,
      5
    );
    this.mainMenu.title = 'MENU';

    // Create character selection menu (right side)
    this.characterMenu = new Menu(
      [],
      128,
      40,
      112,
      4
    );
  }

  // =========================================================================
  // Scene Interface
  // =========================================================================

  init(): void {
    this.state.level = 'main';
    this.state.selectedCharacter = 0;
    this.state.returnToWorld = false;
    this.mainMenu.reset();
    this.updateCharacterMenu();
  }

  update(_deltaTime: number): void {
    // Menu doesn't need frame updates
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.renderer.setContext(ctx);

    // Clear with dark background
    this.renderer.clear('#000000');

    // Render based on current level
    switch (this.state.level) {
      case 'main':
        this.renderMainMenu(ctx);
        break;
      case 'status':
        this.renderStatusScreen(ctx);
        break;
      case 'items':
      case 'items_target':
        this.renderItemsScreen(ctx);
        break;
      case 'magic':
        this.renderMagicScreen(ctx);
        break;
      case 'equip':
      case 'equip_slot':
      case 'equip_select':
        this.renderEquipScreen(ctx);
        break;
      case 'config':
        this.renderConfigScreen(ctx);
        break;
    }
  }

  handleInput(input: GameInput): void {
    if (this.state.level === 'main') {
      this.handleMainMenuInput(input);
    } else if (this.state.level === 'items') {
      this.handleItemsMenuInput(input);
    } else if (this.state.level === 'items_target') {
      this.handleItemsTargetInput(input);
    } else if (this.state.level === 'equip') {
      this.handleEquipMenuInput(input);
    } else if (this.state.level === 'equip_slot') {
      this.handleEquipSlotInput(input);
    } else if (this.state.level === 'equip_select') {
      this.handleEquipSelectInput(input);
    } else {
      this.handleSubMenuInput(input);
    }
  }

  cleanup(): void {
    // Nothing to cleanup
  }

  // =========================================================================
  // Main Menu
  // =========================================================================

  private renderMainMenu(_ctx: CanvasRenderingContext2D): void {
    // Title
    this.renderer.drawCenteredText('MAIN MENU', 128, 16, {
      font: 'ff1',
      fontSize: 12,
      color: '#ffffff',
    });

    // Main menu
    this.mainMenu.render(this.renderer);

    // Character selection window
    this.renderer.drawWindow(128, 40, 112, 180);
    this.renderer.drawText('SELECT CHARACTER', 136, 48, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
    });

    // Render party members
    this.party.forEach((character, index) => {
      const y = 68 + index * 40;
      this.renderCharacterCard(character, 136, y, index === this.state.selectedCharacter);
    });

    // Instructions
    this.renderer.drawText('[Z] SELECT  [X] BACK', 16, 220, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });
  }

  private handleMainMenuInput(input: GameInput): void {
    if (input.justPressed.has(GameKey.UP)) {
      this.mainMenu.moveUp();
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.mainMenu.moveDown();
    } else if (input.justPressed.has(GameKey.LEFT) || input.justPressed.has(GameKey.RIGHT)) {
      // Switch between menu and character selection
      // For now, just change character
      if (input.justPressed.has(GameKey.LEFT)) {
        this.state.selectedCharacter = Math.max(0, this.state.selectedCharacter - 1);
      } else {
        this.state.selectedCharacter = Math.min(this.party.length - 1, this.state.selectedCharacter + 1);
      }
    } else if (input.justPressed.has(GameKey.A)) {
      // Select menu item
      const selected = this.mainMenu.select();
      if (selected) {
        this.enterSubMenu(selected.value as MenuLevel);
      }
    } else if (input.justPressed.has(GameKey.B) || input.justPressed.has(GameKey.START)) {
      // Close menu, return to world
      this.state.returnToWorld = true;
      // Notify callback with updated state
      this.notifyMenuClose();
    }
  }

  private enterSubMenu(level: MenuLevel): void {
    this.state.level = level;
  }

  // =========================================================================
  // Status Screen
  // =========================================================================

  private renderStatusScreen(_ctx: CanvasRenderingContext2D): void {
    const character = this.party[this.state.selectedCharacter];
    if (!character) return;

    // Title
    this.renderer.drawWindow(8, 8, 240, 224);
    this.renderer.drawText('STATUS', 16, 16, {
      font: 'ff1',
      fontSize: 12,
      color: '#ffffff',
    });

    // Character name and class
    this.renderer.drawText(CLASS_INFO[character.class]?.name || 'Unknown', 16, 36, {
      font: 'ff1',
      fontSize: 10,
      color: '#ffff00',
    });

    // Stats
    const statsX = 16;
    let statsY = 56;
    const lineHeight = 16;

    const stats = [
      { label: 'HP', value: `${character.currentHP} / ${character.maxHP}` },
      { label: 'MP', value: `${character.currentMP} / ${character.maxMP}` },
      { label: 'LV', value: character.level.toString() },
      { label: 'EXP', value: character.experience.toString() },
      { label: 'STR', value: character.strength.toString() },
      { label: 'AGI', value: character.agility.toString() },
      { label: 'INT', value: character.intelligence.toString() },
      { label: 'VIT', value: character.vitality.toString() },
      { label: 'LUCK', value: character.luck.toString() },
    ];

    stats.forEach((stat, index) => {
      const y = statsY + index * lineHeight;
      this.renderer.drawText(stat.label, statsX, y, {
        font: 'ff1',
        fontSize: 8,
        color: '#aaaaaa',
      });
      this.renderer.drawText(stat.value, statsX + 60, y, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
      });
    });

    // Equipment
    const equipX = 140;
    let equipY = 56;

    this.renderer.drawText('EQUIPMENT', equipX, equipY, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffff00',
    });
    equipY += lineHeight;

    const equipment = [
      { label: 'WEAPON', id: character.weapon },
      { label: 'ARMOR', id: character.armor },
      { label: 'HELMET', id: character.helmet },
      { label: 'GLOVES', id: character.gloves },
      { label: 'ACCESS', id: character.accessory },
    ];

    equipment.forEach((equip) => {
      this.renderer.drawText(equip.label, equipX, equipY, {
        font: 'ff1',
        fontSize: 8,
        color: '#aaaaaa',
      });
      const item = getItem(equip.id);
      this.renderer.drawText(item?.name || 'NONE', equipX + 60, equipY, {
        font: 'ff1',
        fontSize: 8,
        color: item ? '#ffffff' : '#666666',
      });
      equipY += lineHeight;
    });

    // Equipment stats bonus
    equipY += 8;
    const equipStats = EquipmentSystem.calculateEquipmentStats(character);
    this.renderer.drawText('EQUIPMENT BONUS', equipX, equipY, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffff00',
    });
    equipY += lineHeight;
    this.renderer.drawText(`ATK +${equipStats.attack}`, equipX, equipY, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });
    equipY += 12;
    this.renderer.drawText(`DEF +${equipStats.defense}`, equipX, equipY, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });

    // Navigation
    this.renderNavigation();
  }

  // =========================================================================
  // Items Screen
  // =========================================================================

  private renderItemsScreen(_ctx: CanvasRenderingContext2D): void {
    this.renderer.drawWindow(8, 8, 240, 224);
    this.renderer.drawText('ITEMS', 16, 16, {
      font: 'ff1',
      fontSize: 12,
      color: '#ffffff',
    });

    // Get consumable items
    const consumables = this.inventory.getConsumables();

    if (consumables.length === 0) {
      this.renderer.drawText('(No items)', 16, 48, {
        font: 'ff1',
        fontSize: 8,
        color: '#aaaaaa',
      });
    } else {
      // Render item list
      let itemY = 48;
      const lineHeight = 16;

      consumables.forEach((slot, index) => {
        const item = getItem(slot.itemId);
        if (!item) return;

        const isSelected = this.state.itemsMenu && this.state.itemsMenu.selectedIndex === index;

        // Draw cursor
        if (isSelected) {
          this.renderer.drawCursor(16, itemY + 2);
        }

        // Item name
        this.renderer.drawText(item.name, 28, itemY, {
          font: 'ff1',
          fontSize: 8,
          color: '#ffffff',
        });

        // Quantity
        this.renderer.drawText(`x${slot.quantity}`, 180, itemY, {
          font: 'ff1',
          fontSize: 8,
          color: '#aaaaaa',
        });

        itemY += lineHeight;
      });

      // Show description of selected item
      if (this.state.itemsMenu) {
        const selectedSlot = consumables[this.state.itemsMenu.selectedIndex];
        if (selectedSlot) {
          const item = getItem(selectedSlot.itemId);
          if (item) {
            this.renderer.drawWindow(8, 180, 240, 44);
            this.renderer.drawText(item.description, 16, 188, {
              font: 'ff1',
              fontSize: 8,
              color: '#ffffff',
            });
          }
        }
      }
    }

    // Show character target if in target selection mode
    if (this.state.level === 'items_target') {
      this.renderer.drawWindow(128, 40, 112, 140);
      this.renderer.drawText('USE ON WHO?', 136, 48, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffff00',
      });

      this.party.forEach((character, index) => {
        const y = 68 + index * 32;
        const isSelected = index === this.state.selectedCharacter;

        if (isSelected) {
          this.renderer.drawCursor(132, y + 2);
        }

        this.renderer.drawText(CLASS_INFO[character.class].name, 144, y, {
          font: 'ff1',
          fontSize: 8,
          color: '#ffffff',
        });
        this.renderer.drawText(`HP ${character.currentHP}/${character.maxHP}`, 144, y + 10, {
          font: 'ff1',
          fontSize: 8,
          color: '#aaaaaa',
        });
      });
    }

    this.renderer.drawText('[Z] USE  [X] BACK', 16, 220, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });
  }

  // =========================================================================
  // Magic Screen
  // =========================================================================

  private renderMagicScreen(_ctx: CanvasRenderingContext2D): void {
    const character = this.party[this.state.selectedCharacter];
    if (!character) return;

    this.renderer.drawWindow(8, 8, 240, 224);
    this.renderer.drawText('MAGIC', 16, 16, {
      font: 'ff1',
      fontSize: 12,
      color: '#ffffff',
    });

    this.renderer.drawText(CLASS_INFO[character.class]?.name || 'Unknown', 16, 36, {
      font: 'ff1',
      fontSize: 10,
      color: '#ffff00',
    });

    // TODO: Render available spells based on class and level
    this.renderer.drawText('(No spells)', 16, 56, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });

    this.renderNavigation();
  }

  // =========================================================================
  // Equip Screen
  // =========================================================================

  private renderEquipScreen(_ctx: CanvasRenderingContext2D): void {
    const character = this.party[this.state.selectedCharacter];
    if (!character) return;

    this.renderer.drawWindow(8, 8, 240, 224);
    this.renderer.drawText('EQUIP', 16, 16, {
      font: 'ff1',
      fontSize: 12,
      color: '#ffffff',
    });

    this.renderer.drawText(CLASS_INFO[character.class].name, 16, 36, {
      font: 'ff1',
      fontSize: 10,
      color: '#ffff00',
    });

    // Left side: Equipment slots
    const leftX = 16;
    let leftY = 56;
    const lineHeight = 16;

    const slots = [
      { label: 'WEAPON', id: character.weapon, type: ItemType.WEAPON },
      { label: 'ARMOR', id: character.armor, type: ItemType.ARMOR },
      { label: 'HELMET', id: character.helmet, type: ItemType.HELMET },
      { label: 'GLOVES', id: character.gloves, type: ItemType.GLOVES },
      { label: 'ACCESS', id: character.accessory, type: ItemType.ACCESSORY },
    ];

    slots.forEach((slot, index) => {
      const isSelected = this.state.equipSlotMenu &&
                        this.state.equipSlotMenu.selectedIndex === index;

      if (isSelected && this.state.level === 'equip_slot') {
        this.renderer.drawCursor(leftX, leftY + 2);
      }

      this.renderer.drawText(slot.label, leftX + 12, leftY, {
        font: 'ff1',
        fontSize: 8,
        color: '#aaaaaa',
      });

      const item = getItem(slot.id);
      this.renderer.drawText(item?.name || 'NONE', leftX + 70, leftY, {
        font: 'ff1',
        fontSize: 8,
        color: item ? '#ffffff' : '#666666',
      });

      leftY += lineHeight;
    });

    // Right side: Available equipment for selected slot
    if (this.state.level === 'equip_select' && this.state.selectedEquipSlot !== undefined) {
      this.renderer.drawWindow(128, 40, 112, 180);
      this.renderer.drawText('SELECT EQUIPMENT', 136, 48, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffff00',
      });

      const availableEquip = this.inventory.getItemsByType(this.state.selectedEquipSlot);

      let equipY = 68;
      availableEquip.forEach((slot, index) => {
        const item = getItem(slot.itemId);
        if (!item) return;

        const isSelected = this.state.equipSelectMenu &&
                          this.state.equipSelectMenu.selectedIndex === index;
        const canEquip = EquipmentSystem.canEquip(character, slot.itemId).canEquip;

        if (isSelected) {
          this.renderer.drawCursor(132, equipY + 2);
        }

        this.renderer.drawText(item.name, 144, equipY, {
          font: 'ff1',
          fontSize: 8,
          color: canEquip ? '#ffffff' : '#666666',
        });

        // Show stat comparison
        if (isSelected) {
          const comparison = EquipmentSystem.compareEquipment(character, slot.itemId);
          if (comparison.valid) {
            const color = comparison.difference > 0 ? '#00ff00' :
                         comparison.difference < 0 ? '#ff0000' : '#ffffff';
            const sign = comparison.difference > 0 ? '+' : '';
            this.renderer.drawText(`${sign}${comparison.difference}`, 200, equipY, {
              font: 'ff1',
              fontSize: 8,
              color,
            });
          }
        }

        equipY += lineHeight;
      });

      // Add REMOVE option
      const removeIndex = availableEquip.length;
      const isRemoveSelected = this.state.equipSelectMenu &&
                              this.state.equipSelectMenu.selectedIndex === removeIndex;

      if (isRemoveSelected) {
        this.renderer.drawCursor(132, equipY + 2);
      }
      this.renderer.drawText('REMOVE', 144, equipY, {
        font: 'ff1',
        fontSize: 8,
        color: '#ff8888',
      });
    }

    // Show stats
    const statsY = 140;
    const equipStats = EquipmentSystem.calculateEquipmentStats(character);

    this.renderer.drawText('TOTAL STATS', leftX, statsY, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffff00',
    });
    this.renderer.drawText(`ATK: ${EquipmentSystem.calculateAttackPower(character)}`, leftX, statsY + 16, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
    });
    this.renderer.drawText(`DEF: ${EquipmentSystem.calculateDefense(character)}`, leftX, statsY + 28, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
    });
    this.renderer.drawText(`ACC: ${equipStats.accuracy}`, leftX, statsY + 40, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
    });

    this.renderer.drawText('[Z] SELECT  [X] BACK', 16, 220, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });
  }

  // =========================================================================
  // Config Screen
  // =========================================================================

  private renderConfigScreen(_ctx: CanvasRenderingContext2D): void {
    this.renderer.drawWindow(8, 8, 240, 224);
    this.renderer.drawText('CONFIG', 16, 16, {
      font: 'ff1',
      fontSize: 12,
      color: '#ffffff',
    });

    const options = [
      'MESSAGE SPEED: NORMAL',
      'BATTLE SPEED: NORMAL',
      'SOUND: ON',
      'MUSIC: ON',
    ];

    options.forEach((option, index) => {
      this.renderer.drawText(option, 16, 48 + index * 20, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
      });
    });

    this.renderNavigation();
  }

  // =========================================================================
  // Items Menu Input
  // =========================================================================

  private handleItemsMenuInput(input: GameInput): void {
    const consumables = this.inventory.getConsumables();

    if (consumables.length === 0) {
      if (input.justPressed.has(GameKey.B)) {
        this.state.level = 'main';
      }
      return;
    }

    // Initialize menu if needed
    if (!this.state.itemsMenu) {
      const items: MenuItem[] = consumables.map(slot => {
        const item = getItem(slot.itemId);
        return {
          label: item?.name || 'Unknown',
          value: slot.itemId,
          enabled: true,
        };
      });
      this.state.itemsMenu = new Menu(items, 16, 48, 200, 8);
    }

    if (input.justPressed.has(GameKey.UP)) {
      this.state.itemsMenu.moveUp();
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.state.itemsMenu.moveDown();
    } else if (input.justPressed.has(GameKey.A)) {
      // Select item to use
      const selected = this.state.itemsMenu.select();
      if (selected) {
        this.state.selectedItem = selected.value;
        this.state.level = 'items_target';
        this.state.selectedCharacter = 0;
      }
    } else if (input.justPressed.has(GameKey.B)) {
      this.state.level = 'main';
      this.state.itemsMenu = undefined;
    }
  }

  private handleItemsTargetInput(input: GameInput): void {
    if (input.justPressed.has(GameKey.UP)) {
      this.state.selectedCharacter = Math.max(0, this.state.selectedCharacter - 1);
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.state.selectedCharacter = Math.min(this.party.length - 1, this.state.selectedCharacter + 1);
    } else if (input.justPressed.has(GameKey.A)) {
      // Use item on selected character
      if (this.state.selectedItem) {
        const result = this.inventory.useItem(this.state.selectedItem, false);
        if (result.success && result.effect) {
          // Apply effect to character
          const character = this.party[this.state.selectedCharacter];
          if (character) {
            const hpBefore = character.currentHP;
            const mpBefore = character.currentMP;
            this.applyItemEffect(character, result.effect);
            console.log(`[MenuScene] Item used on ${this.state.selectedCharacter}: HP ${hpBefore}->${character.currentHP}, MP ${mpBefore}->${character.currentMP}`);
          }
        }
        // Return to items menu
        this.state.level = 'items';
        this.state.itemsMenu = undefined;
        this.state.selectedItem = undefined;
      }
    } else if (input.justPressed.has(GameKey.B)) {
      this.state.level = 'items';
      this.state.selectedItem = undefined;
    }
  }

  // =========================================================================
  // Equip Menu Input
  // =========================================================================

  private handleEquipMenuInput(_input: GameInput): void {
    const character = this.party[this.state.selectedCharacter];
    if (!character) return;

    // Initialize equip slot menu
    if (!this.state.equipSlotMenu) {
      const items: MenuItem[] = [
        { label: 'WEAPON', value: ItemType.WEAPON, enabled: true },
        { label: 'ARMOR', value: ItemType.ARMOR, enabled: true },
        { label: 'HELMET', value: ItemType.HELMET, enabled: true },
        { label: 'GLOVES', value: ItemType.GLOVES, enabled: true },
        { label: 'ACCESSORY', value: ItemType.ACCESSORY, enabled: true },
      ];
      this.state.equipSlotMenu = new Menu(items, 16, 56, 200, 5);
      this.state.level = 'equip_slot';
    }
  }

  private handleEquipSlotInput(input: GameInput): void {
    if (!this.state.equipSlotMenu) return;

    if (input.justPressed.has(GameKey.UP)) {
      this.state.equipSlotMenu.moveUp();
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.state.equipSlotMenu.moveDown();
    } else if (input.justPressed.has(GameKey.LEFT)) {
      this.state.selectedCharacter = Math.max(0, this.state.selectedCharacter - 1);
    } else if (input.justPressed.has(GameKey.RIGHT)) {
      this.state.selectedCharacter = Math.min(this.party.length - 1, this.state.selectedCharacter + 1);
    } else if (input.justPressed.has(GameKey.A)) {
      // Select equipment slot
      const selected = this.state.equipSlotMenu.select();
      if (selected) {
        this.state.selectedEquipSlot = selected.value as ItemType;
        this.state.level = 'equip_select';
        this.createEquipSelectMenu();
      }
    } else if (input.justPressed.has(GameKey.B)) {
      this.state.level = 'main';
      this.state.equipSlotMenu = undefined;
    }
  }

  private handleEquipSelectInput(input: GameInput): void {
    if (!this.state.equipSelectMenu || this.state.selectedEquipSlot === undefined) return;

    const character = this.party[this.state.selectedCharacter];
    if (!character) return;

    const availableEquip = this.inventory.getItemsByType(this.state.selectedEquipSlot);

    if (input.justPressed.has(GameKey.UP)) {
      this.state.equipSelectMenu.moveUp();
    } else if (input.justPressed.has(GameKey.DOWN)) {
      this.state.equipSelectMenu.moveDown();
    } else if (input.justPressed.has(GameKey.A)) {
      const selectedIndex = this.state.equipSelectMenu.selectedIndex;

      // Check if REMOVE was selected
      if (selectedIndex === availableEquip.length) {
        const result = EquipmentSystem.unequipItem(character, this.state.selectedEquipSlot);
        if (result.success && result.unequippedItem) {
          this.inventory.addItem(result.unequippedItem, 1);
        }
      } else {
        // Equip selected item
        const slot = availableEquip[selectedIndex];
        if (slot) {
          const result = EquipmentSystem.equipItem(character, slot.itemId);
          if (result.success) {
            // Remove from inventory
            this.inventory.removeItem(slot.itemId, 1);
            // Add unequipped item back to inventory
            if (result.unequippedItem) {
              this.inventory.addItem(result.unequippedItem, 1);
            }
          }
        }
      }

      // Return to slot selection
      this.state.level = 'equip_slot';
      this.state.equipSelectMenu = undefined;
    } else if (input.justPressed.has(GameKey.B)) {
      this.state.level = 'equip_slot';
      this.state.equipSelectMenu = undefined;
    }
  }

  // =========================================================================
  // Sub Menu Input
  // =========================================================================

  private handleSubMenuInput(input: GameInput): void {
    // Common navigation for all sub-menus
    if (input.justPressed.has(GameKey.B)) {
      // Return to main menu
      this.state.level = 'main';
      return;
    }

    if (input.justPressed.has(GameKey.LEFT)) {
      this.state.selectedCharacter = Math.max(0, this.state.selectedCharacter - 1);
    } else if (input.justPressed.has(GameKey.RIGHT)) {
      this.state.selectedCharacter = Math.min(this.party.length - 1, this.state.selectedCharacter + 1);
    }
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  private createEquipSelectMenu(): void {
    if (this.state.selectedEquipSlot === undefined) return;

    const availableEquip = this.inventory.getItemsByType(this.state.selectedEquipSlot);

    const items: MenuItem[] = availableEquip.map(slot => {
      const item = getItem(slot.itemId);
      return {
        label: item?.name || 'Unknown',
        value: slot.itemId,
        enabled: true,
      };
    });

    // Add REMOVE option
    items.push({
      label: 'REMOVE',
      value: 0,
      enabled: true,
    });

    this.state.equipSelectMenu = new Menu(items, 136, 68, 100, 8);
  }

  private applyItemEffect(character: Character, effect: NonNullable<ReturnType<InventorySystem['useItem']>['effect']>): void {
    switch (effect.type) {
      case 'heal':
        character.currentHP = Math.min(character.maxHP, character.currentHP + effect.value);
        break;

      case 'restore':
        character.currentMP = Math.min(character.maxMP, character.currentMP + effect.value);
        break;

      case 'cure_status':
        if (effect.statusEffect !== undefined) {
          character.status &= ~effect.statusEffect;
        }
        break;

      case 'revive':
        character.status &= ~4; // Remove dead status
        character.currentHP = Math.floor(character.maxHP * effect.value / 100);
        break;

      case 'party_heal':
        // Heal entire party
        this.party.forEach(char => {
          if (effect.value >= 100) {
            char.currentHP = char.maxHP;
            char.currentMP = char.maxMP;
            char.status = 0;
          } else {
            char.currentHP = Math.min(char.maxHP, char.currentHP + effect.value);
          }
        });
        break;
    }
  }

  private renderCharacterCard(character: Character, x: number, y: number, selected: boolean): void {
    // Selection highlight
    if (selected) {
      this.renderer.drawSelectionBox(x - 4, y - 4, 96, 32);
    }

    // Class name
    this.renderer.drawText(CLASS_INFO[character.class]?.name || 'Unknown', x, y, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffff00',
    });

    // HP
    this.renderer.drawText(`HP ${character.currentHP}/${character.maxHP}`, x, y + 10, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
    });

    // Level
    this.renderer.drawText(`LV ${character.level}`, x, y + 20, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });
  }

  private renderNavigation(): void {
    this.renderer.drawText('[X] BACK  [<>] CHAR', 16, 220, {
      font: 'ff1',
      fontSize: 8,
      color: '#aaaaaa',
    });
  }

  private updateCharacterMenu(): void {
    const items: MenuItem[] = this.party.map((char, index) => ({
      label: `${CLASS_INFO[char.class]?.name || 'Unknown'} LV${char.level}`,
      enabled: true,
      value: index,
    }));
    this.characterMenu.setItems(items);
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Set party data
   */
  setParty(party: Character[]): void {
    this.party = party;
    this.updateCharacterMenu();
  }

  /**
   * Check if should return to world
   */
  shouldReturnToWorld(): boolean {
    return this.state.returnToWorld;
  }

  /**
   * Reset return flag
   */
  resetReturnFlag(): void {
    this.state.returnToWorld = false;
  }

  /**
   * Get selected character
   */
  getSelectedCharacter(): number {
    return this.state.selectedCharacter;
  }

  /**
   * Set inventory data from game state
   */
  setInventory(itemIds: number[], quantities: number[]): void {
    this.inventory.loadInventory(itemIds, quantities);
  }

  /**
   * Get inventory data for game state
   */
  getInventory(): { itemIds: number[]; quantities: number[] } {
    return this.inventory.exportInventory();
  }

  /**
   * Get inventory system for external use
   */
  getInventorySystem(): InventorySystem {
    return this.inventory;
  }

  /**
   * Register callback for when menu closes - syncs state back to Game.tsx
   */
  onMenuClose(callback: (result: MenuStateResult) => void): void {
    this.menuCloseCallback = callback;
  }

  /**
   * Get current party state (for syncing)
   */
  getParty(): Character[] {
    return this.party;
  }

  /**
   * Trigger the menu close callback with current state
   * Called when returning to world
   */
  private notifyMenuClose(): void {
    if (this.menuCloseCallback) {
      this.menuCloseCallback({
        party: [...this.party],
        inventory: this.inventory.exportInventory(),
      });
    }
  }

  /**
   * External trigger for menu close (called by GameEngine when START is pressed)
   * Public wrapper so GameEngine can invoke the callback
   */
  notifyMenuCloseExternal(): void {
    console.log('[MenuScene] Menu close triggered externally');
    this.notifyMenuClose();
  }
}
